import { Epic, StateObservable } from 'redux-observable'
import { Observable, of, timer } from 'rxjs'
import { concatMap, delayWhen, filter, map, mergeMap, pluck, retryWhen } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { get, omit } from 'lodash-es'
import * as Sentry from '@sentry/react'
import { EpicDependencies, RootState } from 'src/state/types'
import omitIfEmpty from '../../../utils/omitIfEmpty'
import { forkEpic } from 'utils/epics'
import { propertyApiEvents } from '../property'
import { PropertyEvent } from '../property/state'
import { partyApiEpics, partyApiEvents, partyApiSelectors } from '../party'
import { PartyEvent } from '../party/state'
import { TPartyPayload } from '../party/types'
import { events as reconApiEvents, ReconEvent } from '../recon/state'
import { userApiSelectors } from '../user'
import { multiUserOperators } from '../../multiUser/state'
import { events as notificationEvents, NotificationEvent } from 'modules/notificationCenter/state'
import * as invoiceApiConstants from '../invoice/constants'
import { ENDPOINTS, leaseTypes } from './constants'
import { events, PortfolioEvent } from './state'
import * as selectors from './selectors'
import * as transformers from './transformers'
import { portfolioApiSelectors, portfolioApiUtils } from '.'
import {
  TApplicationsResponse,
  TInvoiceSchedule,
  TInvoiceTemplate,
  TPortfolio,
  TTerminationReasonSummary,
} from './types'

type PortfolioEpic = Epic<PortfolioEvent, PortfolioEvent, RootState, EpicDependencies>

/**
 * A `TPartyPayload` is the wrapper encoding `{ person } | { company }`; an already-saved
 * party carries an `id` inside that wrapper.
 */
const partyIdOf = (values: TPartyPayload): string | undefined =>
  ((values.person ?? values.company) as { id?: string } | undefined)?.id

/**
 * Creates a party through a forked `apiCreateParty` and, once it comes back with an id,
 * emits the create-success event followed by whatever the caller wants to do with it.
 * `forkEpic` is untyped, so the emitted action is narrowed here rather than at each site.
 */
const createPartyThen = (
  dependencies: EpicDependencies,
  state$: StateObservable<RootState>,
  values: TPartyPayload,
  andThen: (partyId: string) => PortfolioEvent[],
): Observable<PortfolioEvent | PartyEvent> =>
  (
    forkEpic(
      partyApiEpics.apiCreateParty,
      dependencies,
      state$,
      partyApiEvents.createParty_request(values),
    ) as Observable<{ payload: TPartyPayload & { id?: string } }>
  ).pipe(
    concatMap(res =>
      of(res).pipe(
        pluck('payload'),
        mergeMap((payload): (PortfolioEvent | PartyEvent)[] => {
          const partyId = partyIdOf(payload)
          return partyId ? [partyApiEvents.createParty_success(payload), ...andThen(partyId)] : []
        }),
      ),
    ),
  )

export const apiCreatePortfolio: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.createPortfolio_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.BASE_V2, state$, action.payload, {}).pipe(
        pluck('response'),
        map(res => events.createPortfolio_success(transformers.transformGetPortfolioResponse(res))),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiCreatePortfolio'),
  )

export const getStatus: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.getStatus_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.GET_STATUS, state$, action.payload, {}).pipe(
        pluck('response'),
        map(events.getStatus_success),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/getStatus'),
  )

export const apiFetchEntity: PortfolioEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.portfolio_request.type)),
    // A missing id would be interpolated into the path as the string "undefined" and answered
    // with an error by the portfolio service.
    filter(action => Boolean(action.payload)),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_PORTFOLIO, state$, { id: action.payload }).pipe(
        pluck('response'),
        map(res => events.portfolio_success(transformers.transformGetPortfolioResponse(res))),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiFetchEntity'),
  )

export const apiFetchList: PortfolioEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.portfolios_request.type)),
    multiUserOperators.filterCurrentAgencyId(state$),
    mergeMap(action =>
      get(ENDPOINTS.BASE, state$, action.payload).pipe(
        pluck('response'),
        map((res: unknown[]) => events.portfolios_success(transformers.transformGetListResponse(res))),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiFetchList'),
  )

export const apiFetchSummaries: PortfolioEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.portfolioSummaries_request.type)),
    multiUserOperators.filterCurrentAgencyId(state$),
    mergeMap(action =>
      get(ENDPOINTS.SUMMARIES, state$, {}).pipe(
        pluck('response'),
        map(res => events.portfolioSummaries_success(transformers.transformGetSummariesResponse(res))),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiFetchSummaries'),
  )

export const apiAmendProperty: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.amendProperty_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.AMEND_PROPERTY, state$, body, params).pipe(
        pluck('response'),
        map(res => {
          const previousPropertyId = portfolioApiSelectors.getPortfolioById(state$.value)(params.id)?.propertyId
          return events.amendProperty_success(transformers.transformGetPortfolioResponse(res), {
            previousPropertyId,
          })
        }),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiAmendProperty'),
  )

export const apiAmendTerms: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.amendTerms_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      const portfolioId = params?.id

      if (!portfolioId) {
        const id = userApiSelectors.getUserId(state$.value)
        const email = userApiSelectors.getUserEmail(state$.value)
        const username = userApiSelectors.getUserFirstName(state$.value)
        Sentry.captureEvent({
          message: 'Portfolio ID is undefined in apiAmendTerms epic.',
          environment: process.env.NODE_ENV,
          user: { id: id ?? undefined, email, username },
        })
        return []
      }

      return post(ENDPOINTS.AMEND_TERMS, state$, body, params).pipe(
        pluck('response'),
        map(res => events.amendTerms_success(transformers.transformGetPortfolioResponse(res), { portfolioId })),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiAmendTerms'),
  )

export const apiAmendRentAndFees: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.amendRentAndFees_request.type)),
    mergeMap(action => {
      const { params } = action.payload
      const original = action.payload.body as {
        type: string
        value: { terms: Record<string, unknown> }
      }
      const body = {
        ...original,
        value: {
          ...original.value,
          terms:
            original.type === leaseTypes.managed
              ? {
                  ...omit(original.value.terms, [
                    'firstMonthRentAmount',
                    'leaseFee',
                    'applicationFee',
                    'administrationFee',
                  ]),
                  ...omitIfEmpty('value.terms.firstMonthRentAmount', original, (v: number) => v > 0),
                  ...omitIfEmpty('value.terms.leaseFee', original, (v: { netAmount: number }) => v.netAmount > 0),
                  ...omitIfEmpty('value.terms.applicationFee', original, (v: { netAmount: number }) => v.netAmount > 0),
                  ...omitIfEmpty(
                    'value.terms.administrationFee',
                    original,
                    (v: { netAmount: number }) => v.netAmount > 0,
                  ),
                }
              : original.value.terms,
        },
      }

      const requestBody =
        body.type === leaseTypes.managed
          ? transformers.transformAmendManagedContractRequest(body)
          : transformers.transformAmendUnmanagedContractRequest(body)

      return post(ENDPOINTS.AMEND_CONTRACT, state$, requestBody, params).pipe(
        pluck('response'),
        map(response => events.amendRentAndFees_success(transformers.transformGetPortfolioResponse(response))),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiAmendRentAndFees'),
  )

export const apiAmendSettings: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.amendSettings_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.AMEND_SETTINGS, state$, body, params).pipe(
        pluck('response'),
        map(res =>
          events.amendSettings_success(transformers.transformGetPortfolioResponse(res), { portfolioId: params?.id }),
        ),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiAmendSettings'),
  )

export const apiAmendCommission: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.amendCommission_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.AMEND_COMMISSION, state$, transformers.transformAmendCommissionRequest(body), params).pipe(
        pluck('response'),
        map(response => events.amendCommission_success(transformers.transformGetPortfolioResponse(response))),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiAmendCommission'),
  )

export const apiAmendMetaData: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.amendMetaData_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.AMEND_META_DATA, state$, body, params).pipe(
        pluck('response'),
        map(res => events.amendMetaData_success(transformers.transformGetPortfolioResponse(res))),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiAmendMetaData'),
  )

export const apiAddInvoiceTemplate: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.addInvoiceTemplate_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.CREATE_INVOICE_TEMPLATE, state$, transformers.transformInvoiceTemplate(body), params).pipe(
        pluck('response'),
        map(response =>
          events.addInvoiceTemplate_success(transformers.transformGetPortfolioResponse(response), {
            portfolioId: params.id,
          }),
        ),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiAddInvoiceTemplate'),
  )

export const apiUpdateInvoiceTemplate: PortfolioEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateInvoiceTemplate_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return put(ENDPOINTS.UPDATE_INVOICE_TEMPLATE, state$, transformers.transformInvoiceTemplate(body), params).pipe(
        pluck('response'),
        map(res =>
          events.updateInvoiceTemplate_success(transformers.transformGetPortfolioResponse(res), {
            portfolioId: params.id,
          }),
        ),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiUpdateInvoiceTemplate'),
  )

export const apiRemoveInvoiceTemplate: PortfolioEpic = (action$, state$, { remove, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.removeInvoiceTemplate_request.type)),
    mergeMap(action =>
      remove(ENDPOINTS.REMOVE_INVOICE_TEMPLATE, state$, action.payload).pipe(
        pluck('response'),
        map(res =>
          events.removeInvoiceTemplate_success(transformers.transformGetPortfolioResponse(res), {
            portfolioId: action.payload.id,
          }),
        ),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiRemoveInvoiceTemplate'),
  )

export const apiFetchInvoiceSchedules: PortfolioEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invoiceSchedules_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.INVOICE_SCHEDULES, state$, { id: action.payload }).pipe(
        pluck('response'),
        map((response: { schedules: TInvoiceSchedule[] }) =>
          events.invoiceSchedules_success({ response, params: { id: action.payload } }),
        ),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiFetchInvoiceSchedules'),
  )

export const apiRequestApproval: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.approvalRequest_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.REQUEST_APPROVAL, state$, body, params).pipe(
        pluck('response'),
        map(events.approvalRequest_success),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiRequestApproval'),
  )

export const apiApprove: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.approve_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.APPROVE, state$, body, params).pipe(
        pluck('response'),
        map(res =>
          events.approve_success(transformers.transformGetPortfolioResponse(res), { portfolioId: params?.id }),
        ),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiApprove'),
  )

/**
 * Tenants and landlords are prefetched in bulk by
 * portfolios/state/epics#fetchTenantsAndLandlordsWhenPortfolioIsFetched, off this same
 * portfolio_success. A per-id prefetch (prefetchPortfolioTenantsAndLandlords) used to live
 * here too but emitted nothing: selectLandordAndTenantIdsFromPortfolio returns id strings,
 * so uniqBy(ids, 'partyId') collapsed them to a single undefined key that its own filter
 * then dropped. Reinstating it would only duplicate the bulk fetch, so it is gone.
 */
export const prefetchPartiesFromCommission: Epic<
  PortfolioEvent | PartyEvent,
  PartyEvent,
  RootState,
  EpicDependencies
> = action$ =>
  action$.pipe(
    filter(isOfType(events.portfolio_success.type)),
    // One bulk request rather than a party_request per split. Commission agents are
    // routinely also invoice-template beneficiaries, so emitting them individually meant
    // each one got its own GET *and* a slot in the POST from
    // prefetchPartiesFromInvoiceTemplates, which fires off this same portfolio_success.
    map(({ payload }) =>
      Array.from(new Set((payload.commission?.managementFee?.splits ?? []).map(s => s.agentPartyId))).filter(Boolean),
    ),
    filter((partyIds: string[]) => partyIds.length > 0),
    map((partyIds: string[]) => partyApiEvents.parties_request(partyIds)),
    tag('portfolio/epic/prefetchPartiesFromCommission'),
  )

export const prefetchPartiesFromInvoiceTemplates: Epic<
  PortfolioEvent | PartyEvent | ReconEvent,
  PartyEvent | ReconEvent,
  RootState,
  EpicDependencies
> = (action$, state$) =>
  action$.pipe(
    filter(isOfType(events.portfolio_success.type)),
    mergeMap(({ payload }) => {
      const emitted: (PartyEvent | ReconEvent)[] = []
      const templates: TInvoiceTemplate[] = payload.invoiceTemplates ?? []
      const paymentRules = templates.flatMap(t => t.paymentRules ?? [])
      const partyIds = Array.from(
        new Set(paymentRules.map(rule => get(rule, 'beneficiary.value.partyId', '') as string)),
      )

      // Fetch agency party. Used in invoice templates default values
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const agencyParty = partyApiSelectors.getAgencyParty(state$.value)
      if (!agencyParty && currentAgencyId) {
        partyIds.push(currentAgencyId)
      }

      const easyPayReferences = Array.from(
        new Set(paymentRules.map(rule => get(rule, 'beneficiary.value.easyPayReference') as string | undefined)),
      )

      const filteredPartyIds = partyIds.filter(id => id !== '')
      if (filteredPartyIds.length > 0) {
        emitted.push(partyApiEvents.parties_request(filteredPartyIds))
      }

      easyPayReferences
        .filter((ref): ref is string => ref !== undefined)
        .forEach(reference => {
          emitted.push(reconApiEvents.easypayData_request({ reference }))
        })

      return emitted
    }),
    tag('portfolio/epic/prefetchPartiesFromInvoiceTemplates'),
  )

/**
 * @todo move this to UI epic
 */
export const prefetchProperty: Epic<
  PortfolioEvent | PropertyEvent,
  PropertyEvent,
  RootState,
  EpicDependencies
> = action$ =>
  action$.pipe(
    filter(isOfType(events.portfolio_success.type)),
    map(action => action.payload.propertyId),
    filter((id): id is string => id !== undefined && id !== null),
    map(id => propertyApiEvents.property_request({ id })),
    tag('portfolio/epic/prefetchProperty'),
  )

export const apiFetchTerminationReasons: PortfolioEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.terminationReasons_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_TERMINATION_REASONS, state$, {}).pipe(
        pluck('response'),
        map((res: TTerminationReasonSummary[]) => events.terminationReasons_success(res)),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiFetchTerminationReasons'),
  )

export const apiTerminateApprovedLease: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.terminateApprovedLease_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.TERMINATE_APPROVED_LEASE, state$, body, params).pipe(
        pluck('response'),
        map(res => events.terminateApprovedLease_success(transformers.transformGetPortfolioResponse(res))),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiTerminateApprovedLease'),
  )

export const apiDeleteDraftLease: PortfolioEpic = (action$, state$, { remove, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.deleteDraftLease_request.type)),
    mergeMap(action =>
      remove(ENDPOINTS.DELETE_DRAFT_LEASE, state$, { id: action.payload }).pipe(
        pluck('response'),
        map(res => events.deleteDraftLease_success(transformers.transformGetPortfolioResponse(res))),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiDeleteDraftLease'),
  )

export const apiRenew: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.renewal_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.RENEW, state$, transformers.transformRenewRequest(body), params).pipe(
        pluck('response'),
        map(res => events.renewal_success(transformers.transformGetPortfolioResponse(res), params)),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiRenew'),
  )

export const apiCancelRenewal: PortfolioEpic = (action$, state$, { remove, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.cancelRenewal_request.type)),
    mergeMap(action =>
      remove(ENDPOINTS.RENEW, state$, action.payload).pipe(
        pluck('response'),
        map(() => events.cancelRenewal_success(action.payload)),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiCancelRenewal'),
  )

export const apiAmendSegments: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.amendSegments_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.AMEND_SEGMENTS, state$, body, params).pipe(
        pluck('response'),
        map(() => events.amendSegments_success({ ...body, ...params })),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiAmendSegments'),
  )

export const apiUpdateOwnerParties: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateOwnerParties_request.type)),
    mergeMap(action => {
      const { params, body } = action.payload
      return post(ENDPOINTS.UPDATE_OWNER_PARTIES, state$, body, params).pipe(
        pluck('response'),
        map(res => events.updateOwnerParties_success(transformers.transformGetPortfolioResponse(res), params)),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiUpdateOwnerParties'),
  )

export const apiUpdateTenantParties: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateTenantParties_request.type)),
    mergeMap(action => {
      const { params, body } = action.payload
      return post(ENDPOINTS.UPDATE_TENANT_PARTIES, state$, body, params).pipe(
        pluck('response'),
        map(res => events.updateTenantParties_success(transformers.transformGetPortfolioResponse(res), params)),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiUpdateTenantParties'),
  )

export const apiAddPartyAndLinkToLease: Epic<
  PortfolioEvent | PartyEvent,
  PortfolioEvent | PartyEvent,
  RootState,
  EpicDependencies
> = (action$, state$, dependencies) =>
  action$.pipe(
    filter(isOfType(events.addPartyAndLinkToLease_request.type)),
    mergeMap(action => {
      const { id, values, type } = action.payload
      const addTenantOrOwner = (partyId: string) => {
        if (type === 'landlord') {
          const owners = portfolioApiSelectors.getOwnersByPortfolioId(state$.value)(id)
          return events.updateOwnerParties_request({
            params: { id },
            body: portfolioApiUtils.addParty(owners, partyId),
          })
        }
        const tenants = portfolioApiSelectors.getTenantsByPortfolioId(state$.value)(id)
        return events.updateTenantParties_request({
          params: { id },
          body: portfolioApiUtils.addParty(tenants, partyId),
        })
      }

      const existingPartyId = partyIdOf(values)
      if (existingPartyId) {
        return [addTenantOrOwner(existingPartyId)]
      }

      return createPartyThen(dependencies, state$, values, partyId => [addTenantOrOwner(partyId)])
    }),
    tag('portfolio/epics/apiAddPartyAndLinkToLease'),
  )

export const getApplications: PortfolioEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.getApplications_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.LEASE_APPLICATIONS, state$, action.payload).pipe(
        pluck('response'),
        map((res: TApplicationsResponse) =>
          transformers.transformApplicationsResponse(events.getApplications_success(res)),
        ),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/getApplications'),
  )

export const apiAddApplication: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.addApplication_request.type)),
    mergeMap(action => {
      const { params, body } = action.payload
      return post(ENDPOINTS.LEASE_APPLICATIONS, state$, transformers.transformAddApplicationRequest(body), params).pipe(
        pluck('response'),
        map((res: TApplicationsResponse) =>
          transformers.transformApplicationsResponse(events.addApplication_success(res)),
        ),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiAddApplication'),
  )

export const apiAddApplicationAndLinkToLease: Epic<
  PortfolioEvent | PartyEvent | NotificationEvent,
  PortfolioEvent | PartyEvent | NotificationEvent,
  RootState,
  EpicDependencies
> = (action$, state$, dependencies) =>
  action$.pipe(
    filter(isOfType(events.addApplicationAndLinkToLease_request.type)),
    mergeMap(action => {
      const { id, values, applicationFee = 0 } = action.payload

      // Merge new application with existing applications
      const addApplication = (partyId: string) =>
        events.addApplication_request({ params: { id }, body: { partyId, applicationFee } })

      // if the party exists, create application with existing party
      const partyId = partyIdOf(values)
      if (partyId) {
        const isDuplicateApplication = selectors.getApplicationByPartyId(state$.value)(id, partyId)
        return isDuplicateApplication
          ? [
              notificationEvents.addNotification(
                { message: 'Application already exists', type: 'error' },
                // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
                { autoDismiss: true },
              ),
            ]
          : [addApplication(partyId)]
      }

      // if the party doesn't exist, create party and application
      return createPartyThen(dependencies, state$, values, createdPartyId => [addApplication(createdPartyId)])
    }),
    tag('portfolio/epics/apiAddApplicationAndLinkToLease'),
  )

export const apiUpdateApplication: PortfolioEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateApplication_request.type)),
    mergeMap(action => {
      const { id, values, applicationId } = action.payload
      return put(ENDPOINTS.UPDATE_APPLICATION, state$, transformers.transformUpdateApplicationRequest(values), {
        id,
        applicationId,
      }).pipe(
        pluck('response'),
        mergeMap((res): PortfolioEvent[] => [
          events.updateApplication_success(transformers.transformGetPortfolioResponse(res)),
          events.getApplications_request({ id }),
        ]),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/apiUpdateApplication'),
  )

export const apiDeleteApplication: PortfolioEpic = (action$, state$, { remove, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.deleteApplication_request.type)),
    mergeMap(action =>
      remove(ENDPOINTS.DELETE_APPLICATION, state$, action.payload).pipe(
        pluck('response'),
        map((res: TApplicationsResponse) =>
          transformers.transformApplicationsResponse(events.deleteApplication_success(res)),
        ),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiDeleteApplication'),
  )

export const apiAcceptApplication: PortfolioEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.acceptApplication_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.ACCEPT_APPLICATION, state$, null, action.payload).pipe(
        pluck('response'),
        mergeMap((res): PortfolioEvent[] => [
          events.acceptApplication_success(transformers.transformGetPortfolioResponse(res)),
          events.getApplications_request({ id: action.payload.id }),
        ]),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiAcceptApplication'),
  )

export const apiGetBulkInvoicesCsvTemplate: PortfolioEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.bulkInvoicesCsvTemplate_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.BULK_INVOICES_CSV_TEMPLATE, state$, {}).pipe(
        pluck('response'),
        map(events.bulkInvoicesCsvTemplate_success),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiGetBulkInvoicesCsvTemplate'),
  )

export const sendApplicationInvoice: Epic<
  PortfolioEvent | NotificationEvent,
  PortfolioEvent | NotificationEvent,
  RootState,
  EpicDependencies
> = (action$, state$, { post, get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.sendApplicationInvoice_request.type)),
    mergeMap(action =>
      get(invoiceApiConstants.ENDPOINTS.INVOICE_ENTITY, state$, { id: action.payload.applicationId }).pipe(
        retryWhen(errors => errors.pipe(delayWhen(() => timer(1000)))),
        map(() => action),
      ),
    ),
    mergeMap(action => {
      const { portfolioId, applicationId } = action.payload
      const requestPayload = {
        invoiceIds: [applicationId],
      }
      return post(invoiceApiConstants.ENDPOINTS.SEND_INVOICES, state$, requestPayload, {}).pipe(
        pluck('response', 'invoiceResults'),
        mergeMap(() => [
          events.sendApplicationInvoice_success({
            portfolioId,
            applicationId,
            invoiceId: applicationId,
            status: 'InvoiceSent',
          }),
          notificationEvents.addNotification(
            {
              type: 'success',
              message: 'Application Fee invoice sent successfully',
            },
            // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
            { autoDismiss: true },
          ),
        ]),
        catchRestError(action),
      )
    }),
    tag('portfolio/epics/sendApplicationInvoice'),
  )
